import { pb } from './pocketbase';

/**
 * Service pour gérer les catégories via PocketBase
 */
export class CategoryService {
  static collectionName = 'categories';

  /**
   * Récupérer toutes les catégories
   * @param {Object} options - Options de requête (page, perPage, sort, filter)
   * @returns {Promise<Object>} Liste paginée des catégories
   */
  static async getAll(options = {}) {
    try {
      const {
        page = 1,
        perPage = 50,
        sort = 'name',
        filter = ''
      } = options;

      const result = await pb.collection(this.collectionName).getList(page, perPage, {
        sort,
        filter
      });

      return result;
    } catch (error) {
      console.error('Erreur lors de la récupération des catégories:', error);
      throw error;
    }
  }

  /**
   * Récupérer toutes les catégories sans pagination
   * @returns {Promise<Array>} Toutes les catégories
   */
  static async getAllCategories() {
    try {
      console.log('Service: Récupération de toutes les catégories...');
      
      // Ajouter un timeout pour éviter les blocages
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout: Catégories trop longues à charger')), 10000)
      );
      
      const categoriesPromise = pb.collection(this.collectionName).getFullList({
        sort: 'name',
        requestKey: `categories-all-${Date.now()}` // Clé unique
      });
      
      const result = await Promise.race([categoriesPromise, timeoutPromise]);
      
      console.log(`Service: ${result.length} catégories récupérées`);
      return result;
    } catch (error) {
      // Ignorer les erreurs d'annulation automatique
      if (error.name === 'AbortError' || error.message?.includes('aborted')) {
        console.log('Service: Requête annulée pour les catégories');
        return [];
      }
      
      console.error('Erreur lors de la récupération de toutes les catégories:', error);
      
      // Retourner un tableau vide au lieu de throw pour éviter les crashes
      console.warn('Retour d\'un tableau vide pour éviter le crash de l\'application');
      return [];
    }
  }

  /**
   * Récupérer une catégorie par son ID
   * @param {string} id - ID de la catégorie
   * @returns {Promise<Object>} Catégorie trouvée
   */
  static async getById(id) {
    try {
      const category = await pb.collection(this.collectionName).getOne(id);
      return category;
    } catch (error) {
      console.error(`Erreur lors de la récupération de la catégorie ${id}:`, error);
      throw error;
    }
  }

  /**
   * Récupérer une catégorie par son slug
   * @param {string} slug - Slug de la catégorie
   * @returns {Promise<Object>} Catégorie trouvée
   */
  static async getBySlug(slug) {
    try {
      const category = await pb.collection(this.collectionName).getFirstListItem(`slug="${slug}"`);
      return category;
    } catch (error) {
      console.error(`Erreur lors de la récupération de la catégorie avec le slug ${slug}:`, error);
      throw error;
    }
  }

  /**
   * Rechercher des catégories
   * @param {string} query - Terme de recherche
   * @param {Object} options - Options de requête
   * @returns {Promise<Object>} Résultats de recherche
   */
  static async search(query, options = {}) {
    try {
      const {
        page = 1,
        perPage = 20,
        sort = 'name'
      } = options;

      const filter = `name ~ "${query}" || slug ~ "${query}"`;

      const result = await pb.collection(this.collectionName).getList(page, perPage, {
        sort,
        filter
      });

      return result;
    } catch (error) {
      console.error('Erreur lors de la recherche de catégories:', error);
      throw error;
    }
  }

  /**
   * Créer une nouvelle catégorie
   * @param {Object} data - Données de la catégorie
   * @returns {Promise<Object>} Catégorie créée
   */
  static async create(data) {
    try {
      const category = await pb.collection(this.collectionName).create(data);
      return category;
    } catch (error) {
      console.error('Erreur lors de la création de la catégorie:', error);
      throw error;
    }
  }

  /**
   * Mettre à jour une catégorie
   * @param {string} id - ID de la catégorie
   * @param {Object} data - Nouvelles données
   * @returns {Promise<Object>} Catégorie mise à jour
   */
  static async update(id, data) {
    try {
      const category = await pb.collection(this.collectionName).update(id, data);
      return category;
    } catch (error) {
      console.error(`Erreur lors de la mise à jour de la catégorie ${id}:`, error);
      throw error;
    }
  }

  /**
   * Supprimer une catégorie
   * @param {string} id - ID de la catégorie
   * @returns {Promise<boolean>} Succès de la suppression
   */
  static async delete(id) {
    try {
      await pb.collection(this.collectionName).delete(id);
      return true;
    } catch (error) {
      console.error(`Erreur lors de la suppression de la catégorie ${id}:`, error);
      throw error;
    }
  }

  /**
   * Récupérer les catégories avec le nombre d'articles
   * @returns {Promise<Array>} Catégories avec compteur d'articles
   */
  static async getCategoriesWithCount() {
    try {
      // Récupérer toutes les catégories
      const categories = await this.getAllCategories();
      
      // Pour chaque catégorie, compter les articles
      const categoriesWithCount = await Promise.all(
        categories.map(async (category) => {
          try {
            const articlesResult = await pb.collection('articles').getList(1, 1, {
              filter: `category="${category.id}"`
            });
            return {
              ...category,
              articlesCount: articlesResult.totalItems
            };
          } catch (error) {
            console.error(`Erreur lors du comptage des articles pour la catégorie ${category.id}:`, error);
            return {
              ...category,
              articlesCount: 0
            };
          }
        })
      );

      return categoriesWithCount;
    } catch (error) {
      console.error('Erreur lors de la récupération des catégories avec compteur:', error);
      throw error;
    }
  }

  /**
   * Vérifier si une catégorie existe par son slug
   * @param {string} slug - Slug à vérifier
   * @returns {Promise<boolean>} True si la catégorie existe
   */
  static async existsBySlug(slug) {
    try {
      await this.getBySlug(slug);
      return true;
    } catch (error) {
      return false;
    }
  }
}